import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Lock, MapPin, GraduationCap, Briefcase, Brain, UserSearch } from 'lucide-react';
import { toast } from 'sonner';
import { candidatesAPI } from '../../lib/api';

const dur = (m) => {
  if (m === null || m === undefined) return null;
  const a = Math.floor(m / 12); const r = m % 12;
  return [a && `${a} ${a === 1 ? 'ano' : 'anos'}`, r && `${r} ${r === 1 ? 'mês' : 'meses'}`].filter(Boolean).join(' e ') || 'menos de 1 mês';
};

const SummaryCard = ({ s, onViewProfile, loading }) => {
  const tags = [s.is_pcd && 'PCD', s.is_50_plus && '50+', s.is_estagiario && 'Estágio', s.is_aprendiz && 'Aprendiz',
    s.disponibilidade_remoto && `Remoto: ${s.disponibilidade_remoto}`, s.disponibilidade_mudanca && 'Aceita mudança'].filter(Boolean);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-2" data-testid={`summary-card-${s.candidate_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold text-slate-900" data-testid="summary-codigo">{s.codigo}</p>
          <p className="text-sm text-slate-600">{s.cargo}{s.nivel_experiencia ? ` · ${s.nivel_experiencia}` : ''}</p>
        </div>
        {s.novo && <Badge className="bg-emerald-100 text-emerald-700">Novo</Badge>}
      </div>
      <div className="text-xs text-slate-600 space-y-1">
        {s.tempo_experiencia_meses !== null && <p><Briefcase className="w-3.5 h-3.5 inline mr-1" />Experiência total: {dur(s.tempo_experiencia_meses)}</p>}
        {s.experiencias.map((e, i) => (
          <p key={i} className="pl-5">• {e.funcao || 'Função não informada'}{e.meses !== null ? ` — ${dur(e.meses)}` : ''}{e.atual ? ' (atual)' : ''}</p>
        ))}
        {s.formacoes.map((f, i) => (
          <p key={i}><GraduationCap className="w-3.5 h-3.5 inline mr-1" />{[f.nivel, f.curso].filter(Boolean).join(' em ')}{f.em_andamento ? ' (em andamento)' : ''}</p>
        ))}
        {s.disc_profile && <p><Brain className="w-3.5 h-3.5 inline mr-1" />Perfil DISC: {s.disc_profile}</p>}
        {s.local && <p><MapPin className="w-3.5 h-3.5 inline mr-1" />{s.local}</p>}
      </div>
      {s.competencias.length > 0 && (
        <div className="flex flex-wrap gap-1">{s.competencias.map((c) => <span key={c} className="text-[11px] bg-cyan-50 text-cyan-800 rounded-full px-2 py-0.5">{c}</span>)}</div>
      )}
      {tags.length > 0 && <div className="flex flex-wrap gap-1">{tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}</div>}
      <Button size="sm" variant="outline" className="mt-auto" disabled={loading} onClick={() => onViewProfile(s)} data-testid={`summary-view-profile-${s.candidate_id}`}>
        {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserSearch className="w-4 h-4 mr-1" />} Ver perfil e contato
      </Button>
    </div>
  );
};

export const CargoSummariesDialog = ({ group, onClose, onViewProfile }) => {
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState(null);
  useEffect(() => {
    if (!group) return;
    setData(null);
    candidatesAPI.byCargoSummaries(group.key).then((r) => setData(r.data)).catch(() => { toast.error('Erro ao carregar resumos'); onClose(); });
  }, [group]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = async (s) => {
    setOpening(s.candidate_id);
    try {
      const r = await candidatesAPI.byCargoProfile(s.candidate_id);
      onClose();
      onViewProfile(r.data);
    } catch {
      toast.error('Perfil indisponível no momento');
    } finally { setOpening(null); }
  };

  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="cargo-summaries-dialog">
        <DialogHeader>
          <DialogTitle>{group?.cargo} — {group?.total} {group?.total === 1 ? 'currículo' : 'currículos'}</DialogTitle>
          <DialogDescription className="flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" /> Resumos sem nome para comparar. Clique em "Ver perfil e contato" para ver telefone, e-mail e baixar o currículo.
            {group?.variacoes?.length > 0 && ` Inclui: ${group.variacoes.join(', ')}.`}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((s) => <SummaryCard key={s.candidate_id} s={s} onViewProfile={view} loading={opening === s.candidate_id} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
